import { useMemo, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
} from "@altimateai/lego";
import { IconArrowLeft } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { getConflictingTeams, downloadConflictingTeamsCSV } from "@/helpers/apis";
import { DownloadTable } from "@/components/downloadTable";
import type { OwnershipRequestPayload, ConflictingTeamDetail } from "./types";
import { Stack } from "@altimateai/lego";
import { DataTable } from "@/components/tables";
import { TableHeaderType } from "@/components/tables/types";
import { DatatableContextProvider } from "@/components/tables/contexts/DatatableContextProvider";
import { formatCurrencyAmount } from "./ownershipHelpers";

interface ConflictingTeamsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ownershipRequests: OwnershipRequestPayload[];
  startDate: string;
  endDate: string;
  onValidateForm?: () => Promise<boolean>;
  onBack?: () => void;
}

const ConflictingTeamsModal = ({
  open,
  onOpenChange,
  ownershipRequests,
  startDate,
  endDate,
  onValidateForm,
  onBack,
}: ConflictingTeamsModalProps) => {
  const {
    data: conflicts,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["conflicting-teams", ownershipRequests, startDate, endDate],
    queryFn: async () => {
      if (onValidateForm) {
        const isValid = await onValidateForm();
        if (!isValid) {
          throw new Error("Please fill in all required fields before viewing conflicting teams");
        }
      }

      return getConflictingTeams(
        { ownership_requests: ownershipRequests },
        { start_date: startDate, end_date: endDate }
      );
    },
    enabled: open && ownershipRequests.length > 0,
  });

  const handleDownload = useCallback(async () => {
    if (!ownershipRequests.length) return;

    try {
      await downloadConflictingTeamsCSV(
        {
          ownership_requests: ownershipRequests,
        },
        {
          start_date: startDate,
          end_date: endDate,
        }
      );
    } catch (err) {
      console.error("Failed to download conflicting teams CSV:", err);
    }
  }, [ownershipRequests, startDate, endDate]);

  const tableHeaders = useMemo<TableHeaderType<ConflictingTeamDetail>[]>(() => {
    if (!conflicts) return [];

    return [
      {
        id: 1,
        label: "Team(s)",
        uid: "team_list",
        width: "40%",
        render: (item: ConflictingTeamDetail) => (
          <Stack direction="row" gap="sm" wrap>
            {item.team_list.map((team: string, teamIndex: number) => (
              <span
                key={teamIndex}
                className="al-inline-flex al-items-center al-px-2.5 al-py-0.5 al-rounded-md al-bg-blue-100 al-text-blue-800 al-text-sm al-font-medium"
              >
                {team}
              </span>
            ))}
          </Stack>
        ),
      },
      {
        id: 2,
        label: "Conflicting entities",
        uid: "conflicting_entity_count",
        width: "30%",
        render: (item: ConflictingTeamDetail) => (
          <div className="al-text-right al-font-medium">{item.conflicting_entity_count}</div>
        ),
      },
      {
        id: 3,
        label: "Conflicting cost",
        uid: "conflicting_cost",
        width: "30%",
        render: (item: ConflictingTeamDetail) => (
          <div className="al-text-right al-font-medium">
            {formatCurrencyAmount(item.conflicting_cost, conflicts.currency)}
          </div>
        ),
      },
    ];
  }, [conflicts]);

  const tableData = useMemo(() => {
    if (!conflicts) return { items: [] };
    return { items: conflicts.conflicts };
  }, [conflicts]);

  const errorMessage = error instanceof Error ? error.message : "Failed to load conflicting teams";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!al-max-w-5xl al-h-[80vh] al-overflow-x-auto">
        <Stack direction="column" className="al-h-full">
          <Stack direction="row" justify="between" align="center" className="al-flex-shrink-0">
            <DialogHeader>
              <Stack direction="row" align="center" gap="sm">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (onBack) {
                      onBack();
                    } else {
                      onOpenChange(false);
                    }
                  }}
                  className="al-h-8 al-w-8 al-p-0"
                  aria-label="Go back"
                >
                  <IconArrowLeft size={18} />
                </Button>
                <DialogTitle>Conflicting Teams</DialogTitle>
              </Stack>
            </DialogHeader>
            <DownloadTable
              handleDownload={handleDownload}
              buttonProps={{
                disabled: isLoading || !conflicts?.conflicts?.length,
                className: "al-mt-6",
              }}
            />
          </Stack>

          <div className="al-flex-1 al-overflow-hidden">
            {isLoading && (
              <Stack justify="center" align="center" className="al-py-12">
                <p className="al-text-muted-foreground">Loading conflicting teams...</p>
              </Stack>
            )}

            {error && (
              <div className="al-rounded-md al-bg-destructive/10 al-border al-border-destructive/20 al-p-4">
                <p className="al-text-sm al-text-destructive">{errorMessage}</p>
              </div>
            )}

            {!isLoading && !error && conflicts && (
              <>
                {conflicts.conflicts.length === 0 ? (
                  <Stack justify="center" align="center" className="al-py-12">
                    <p className="al-text-muted-foreground">No conflicting teams found</p>
                  </Stack>
                ) : (
                  <DatatableContextProvider>
                    <div className="al-h-full">
                      <DataTable
                        header={tableHeaders}
                        data={tableData}
                        isLoading={isLoading}
                        minWidth={600}
                      />
                    </div>
                  </DatatableContextProvider>
                )}
              </>
            )}
          </div>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ConflictingTeamsModal;
